import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiDownload, FiFilter, FiX,
  FiAlertCircle, FiClock, FiTrendingUp, FiFileText
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { resolveDisplayName, useUsersDirectory } from '../../lib/usersDirectory';

const CARRIERS = [
  'F&G', 'Ethos', 'Ameritas', 'Corebridge', 'Foresters',
  'Mutual of Omaha', 'Quility', 'Banner', 'North American',
  'American National', 'Allianz', 'American Equity', 'Other',
];

const STATUSES = [
  { value: 'application_started', label: 'App Started', color: '#6b7280' },
  { value: 'submitted', label: 'Submitted', color: '#3b82f6' },
  { value: 'pending_requirements', label: 'Pending Req.', color: '#eab308' },
  { value: 'underwriting', label: 'Underwriting', color: '#a855f7' },
  { value: 'approved', label: 'Approved', color: '#22c55e' },
  { value: 'advanced_paid', label: 'Advanced Paid', color: '#059669' },
  { value: 'declined', label: 'Declined', color: '#ef4444' },
  { value: 'cancelled', label: 'Cancelled', color: '#9ca3af' },
];

const STATUS_COLORS = Object.fromEntries(
  STATUSES.map(s => [s.value, s.color])
);

const STATUS_LABELS = Object.fromEntries(
  STATUSES.map(s => [s.value, s.label])
);

function daysSince(dateStr) {
  if (!dateStr) return null;
  return Math.floor(
    (Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24)
  );
}

function fmtPts(val) {
  if (!val) return '—';
  return new Intl.NumberFormat('en-US').format(val) + ' pts';
}

function fmtCurrency(val) {
  if (!val) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(val);
}

function fmtDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function StatCard({ label, value, sub, color }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
      <p className="text-[10px] font-semibold uppercase tracking-wider 
                    text-gray-400 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color || 'text-gray-900'}`}>
        {value}
      </p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function StaleReportTable({ apps, navigate }) {
  if (apps.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <FiAlertCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
        <p className="text-sm">No stale applications found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100">
            {['Client', 'Carrier', 'Status', 'Days in Status',
              'Points', 'Agent', 'Submitted'].map(h => (
              <th key={h} className="text-left px-4 py-3 text-xs font-medium 
                                     text-gray-400 uppercase tracking-wider">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {apps.map(app => {
            const days = daysSince(app.updated_at);
            return (
              <tr
                key={app.id}
                onClick={() => navigate(`/applications/${app.id}`)}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3">
                  <p className="text-sm font-medium text-gray-900">
                    {app.client_name}
                  </p>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {app.carrier || '—'}
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs font-medium px-2 py-1 rounded-full"
                    style={{
                      background: STATUS_COLORS[app.status] + '20',
                      color: STATUS_COLORS[app.status],
                    }}>
                    {STATUS_LABELS[app.status] || app.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-sm font-semibold ${
                    days > 20 ? 'text-red-500' :
                    days > 10 ? 'text-yellow-500' : 'text-gray-700'
                  }`}>
                    {days}d
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {fmtPts(app.target_points)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {resolveDisplayName(app.agent_id, app.agent, app.agent_name) || '—'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {fmtDate(app.submitted_at)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function exportCSV(apps, filename) {
  const headers = [
    'Client', 'Carrier', 'Product', 'Status', 'Points',
    'Premium', 'Policy #', 'Agent', 'Submitted', 'Days in Status'
  ];
  const rows = apps.map(a => [
    a.client_name,
    a.carrier || '',
    a.product || '',
    STATUS_LABELS[a.status] || a.status,
    a.target_points || '',
    a.premium || '',
    a.policy_number || '',
    resolveDisplayName(a.agent_id, a.agent, a.agent_name) || '',
    a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : '',
    daysSince(a.updated_at) || '',
  ]);
  const csv = [headers, ...rows]
    .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ApplicationsReports() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const { users: directoryUsers } = useUsersDirectory();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState('stale');
  const [filterCarrier, setFilterCarrier] = useState('');
  const [filterAgent, setFilterAgent] = useState('');

  const isAdmin = ['admin', 'super_admin', 'manager'].includes(user?.role);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const result = await api.applications.getReportData({
          carrier: filterCarrier || undefined,
          agent_id: filterAgent || undefined,
        });
        setData(result);
      } catch {
        addToast({ type: 'error', message: 'Failed to load report data' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filterCarrier, filterAgent, addToast]);

  const agents = useMemo(() => {
    const map = new Map();
    data.forEach(a => {
      const name = resolveDisplayName(a.agent_id, a.agent, a.agent_name);
      if (a.agent_id && name) {
        map.set(a.agent_id, name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [data, directoryUsers]);

  const staleReports = useMemo(() => ({
    submitted_stale: {
      label: 'Submitted > 10 days',
      icon: FiClock,
      color: 'text-blue-500',
      apps: data.filter(a =>
        a.status === 'submitted' &&
        daysSince(a.updated_at) > 10
      ),
      threshold: 10,
    },
    underwriting_stale: {
      label: 'Underwriting > 7 days',
      icon: FiAlertCircle,
      color: 'text-purple-500',
      apps: data.filter(a =>
        a.status === 'underwriting' &&
        daysSince(a.updated_at) > 7
      ),
      threshold: 7,
    },
    pending_stale: {
      label: 'Pending Requirements > 20 days',
      icon: FiAlertCircle,
      color: 'text-yellow-500',
      apps: data.filter(a =>
        a.status === 'pending_requirements' &&
        daysSince(a.updated_at) > 20
      ),
      threshold: 20,
    },
  }), [data]);

  const statusChart = useMemo(() => {
    return STATUSES.map(s => ({
      name: s.label,
      value: data.filter(a => a.status === s.value).length,
      color: s.color,
      points: data
        .filter(a => a.status === s.value)
        .reduce((sum, a) => sum + (parseFloat(a.target_points) || 0), 0),
    })).filter(s => s.value > 0);
  }, [data]);

  const carrierChart = useMemo(() => {
    const map = new Map();
    data.forEach(a => {
      const c = a.carrier || 'Other';
      if (!map.has(c)) map.set(c, { count: 0, points: 0 });
      const entry = map.get(c);
      entry.count++;
      entry.points += parseFloat(a.target_points) || 0;
    });
    return Array.from(map.entries())
      .map(([name, vals]) => ({ name, ...vals }))
      .sort((a, b) => b.count - a.count);
  }, [data]);

  const agentChart = useMemo(() => {
    const map = new Map();
    data.forEach(a => {
      const name = resolveDisplayName(a.agent_id, a.agent, a.agent_name) || 'Unassigned';
      if (!map.has(name)) map.set(name, { count: 0, points: 0, advanced: 0 });
      const entry = map.get(name);
      entry.count++;
      entry.points += parseFloat(a.target_points) || 0;
      if (a.status === 'advanced_paid') entry.advanced++;
    });
    return Array.from(map.entries())
      .map(([name, vals]) => ({ name, ...vals }))
      .sort((a, b) => b.points - a.points);
  }, [data, directoryUsers]);

  const totalPoints = data.reduce(
    (sum, a) => sum + (parseFloat(a.target_points) || 0), 0
  );
  const advancedPaid = data.filter(a => a.status === 'advanced_paid');
  const advancedPoints = advancedPaid.reduce(
    (sum, a) => sum + (parseFloat(a.target_points) || 0), 0
  );
  const inPipeline = data.filter(a =>
    !['declined', 'cancelled', 'advanced_paid'].includes(a.status)
  );

  const REPORT_TABS = [
    { id: 'stale', label: 'Stale Pipeline', icon: FiClock },
    { id: 'status', label: 'By Status', icon: FiFileText },
    { id: 'carrier', label: 'By Carrier', icon: FiTrendingUp },
    { id: 'agent', label: 'By Agent', icon: FiTrendingUp },
  ];

  const selectCls = `px-3 py-2 bg-white border border-gray-200 rounded-lg 
    text-sm text-gray-700 focus:outline-none focus:border-[#00AAFF] 
    transition-colors`;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 md:p-8 space-y-6 pb-16">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/applications')}
              className="text-gray-400 hover:text-gray-700 transition-colors">
              <FiArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Application Reports
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {data.length} applications · last updated now
              </p>
            </div>
          </div>
          <button
            onClick={() => exportCSV(data, 'applications-report.csv')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 
                       hover:border-gray-400 text-gray-600 hover:text-gray-900 
                       text-sm rounded-lg transition-colors bg-white shadow-sm"
          >
            <FiDownload className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <FiFilter className="w-4 h-4 text-gray-400" />
          <select className={selectCls} value={filterCarrier}
            onChange={e => setFilterCarrier(e.target.value)}>
            <option value="">All Carriers</option>
            {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {isAdmin && agents.length > 0 && (
            <select className={selectCls} value={filterAgent}
              onChange={e => setFilterAgent(e.target.value)}>
              <option value="">All Agents</option>
              {agents.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          )}
          {(filterCarrier || filterAgent) && (
            <button
              onClick={() => { setFilterCarrier(''); setFilterAgent(''); }}
              className="flex items-center gap-1 text-xs text-gray-400 
                         hover:text-gray-700 transition-colors">
              <FiX className="w-3 h-3" /> Reset
            </button>
          )}
        </div>

        {/* Summary stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Total Applications"
            value={data.length}
            sub="all statuses"
          />
          <StatCard
            label="In Pipeline"
            value={inPipeline.length}
            sub="active cases"
            color="text-blue-600"
          />
          <StatCard
            label="Advanced Paid"
            value={advancedPaid.length}
            sub={fmtPts(advancedPoints)}
            color="text-emerald-600"
          />
          <StatCard
            label="Total Points"
            value={fmtPts(totalPoints)}
            sub="all applications"
            color="text-[#00AAFF]"
          />
        </div>

        {/* Report tabs */}
        <div className="bg-white border border-gray-200 rounded-xl 
                        shadow-sm overflow-hidden">
          <div className="flex border-b border-gray-100 px-2">
            {REPORT_TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button key={tab.id}
                  onClick={() => setActiveReport(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3.5 text-sm 
                    font-medium border-b-2 transition-colors ${
                    activeReport === tab.id
                      ? 'border-[#00AAFF] text-[#00AAFF]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}>
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex justify-center py-16">
                <div className="w-6 h-6 border-2 border-[#00AAFF] 
                               border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (

              <>
                {activeReport === 'stale' && (
                  <div className="space-y-8">
                    {Object.values(staleReports).map(report => {
                      const Icon = report.icon;
                      return (
                        <div key={report.label}>
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <Icon className={`w-4 h-4 ${report.color}`} />
                              <h3 className="text-sm font-semibold text-gray-900">
                                {report.label}
                              </h3>
                              <span className="text-xs bg-gray-100 text-gray-600 
                                             px-2 py-0.5 rounded-full font-medium">
                                {report.apps.length}
                              </span>
                            </div>
                            {report.apps.length > 0 && (
                              <button
                                onClick={() => exportCSV(
                                  report.apps,
                                  `stale-${report.label.toLowerCase().replace(/\s+/g, '-')}.csv`
                                )}
                                className="flex items-center gap-1 text-xs 
                                           text-gray-400 hover:text-gray-700">
                                <FiDownload className="w-3 h-3" /> Export
                              </button>
                            )}
                          </div>
                          <StaleReportTable
                            apps={report.apps}
                            navigate={navigate}
                          />
                          {Object.values(staleReports).indexOf(report) <
                           Object.values(staleReports).length - 1 && (
                            <div className="border-t border-gray-100 mt-8" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {activeReport === 'status' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-4">
                          Distribution by status
                        </h3>
                        <ResponsiveContainer width="100%" height={280}>
                          <PieChart>
                            <Pie
                              data={statusChart}
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              dataKey="value"
                              label={({ name, value }) => `${name}: ${value}`}
                              labelLine={false}
                            >
                              {statusChart.map((entry, i) => (
                                <Cell key={i} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-4">
                          Points by status
                        </h3>
                        <div className="space-y-2">
                          {statusChart.map(s => (
                            <div key={s.name}
                              className="flex items-center justify-between 
                                         p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full"
                                  style={{ background: s.color }} />
                                <span className="text-sm text-gray-700">
                                  {s.name}
                                </span>
                                <span className="text-xs text-gray-400">
                                  ({s.value})
                                </span>
                              </div>
                              <span className="text-sm font-medium text-gray-900">
                                {s.points > 0 ? fmtPts(s.points) : '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeReport === 'carrier' && (
                  <div className="space-y-6">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Applications by carrier
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={carrierChart}
                        margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip
                          formatter={(val, name) =>
                            name === 'points' ? fmtPts(val) : val}
                        />
                        <Bar dataKey="count" fill="#00AAFF"
                             name="Applications" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-100">
                            {['Carrier', 'Applications', 'Total Points'].map(h => (
                              <th key={h} className="text-left px-4 py-3 text-xs 
                                font-medium text-gray-400 uppercase tracking-wider">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {carrierChart.map(row => (
                            <tr key={row.name}
                              className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3 text-sm font-medium 
                                            text-gray-900">{row.name}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {row.count}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {row.points > 0 ? fmtPts(row.points) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {activeReport === 'agent' && (
                  <div className="space-y-6">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Production by agent
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={agentChart}
                        margin={{ top: 0, right: 0, left: 0, bottom: 40 }}>
                        <XAxis dataKey="name" tick={{ fontSize: 10 }}
                          angle={-30} textAnchor="end" interval={0} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip
                          formatter={(val, name) =>
                            name === 'points' ? fmtPts(val) : val}
                        />
                        <Bar dataKey="points" fill="#1B4C87"
                             name="Points" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-100">
                            {['Agent', 'Applications',
                              'Total Points', 'Advanced Paid'].map(h => (
                              <th key={h} className="text-left px-4 py-3 
                                text-xs font-medium text-gray-400 
                                uppercase tracking-wider">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {agentChart.map(row => (
                            <tr key={row.name}
                              className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3 text-sm font-medium 
                                            text-gray-900">{row.name}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {row.count}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {row.points > 0 ? fmtPts(row.points) : '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {row.advanced}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
